import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { CircularProgress } from '@material-ui/core';
import { Settings } from '@material-ui/icons';
import { Agent } from './types';
import { getAgentIcon } from './utils';
import { useNavigate } from 'react-router-dom';
import { DarkTooltip } from './DarkTooltip';

const useStyles = makeStyles(theme => ({
  chatHeader: {
    padding: theme.spacing(2.5, 3),
    background:
      'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#ffffff',
  },
  stsHeader: {
    padding: theme.spacing(2, 0),
    backgroundColor: 'transparent',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stsTitle: {
    fontWeight: 600,
    color: '#000000',
    fontSize: '1rem',
    lineHeight: 1.3,
  },
  betaBadge: {
    display: 'inline-block',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 5px',
    borderRadius: '4px',
    lineHeight: 1,
    marginLeft: 6,
    verticalAlign: 'middle',
  },
  stsSubtitle: {
    color: '#616161',
    fontSize: '0.75rem',
  },
  stsActionButton: {
    color: '#616161',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  avatarSmall: {
    width: 38,
    height: 38,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    border: '2px solid white',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  refreshButton: {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  themeToggle: {
    color: '#ffffff',
  },
}));

interface ChatHeaderProps {
  agentConfig: Agent;
  selectedAgent: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  basePath?: string;
  stsMode?: boolean;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  agentConfig,
  selectedAgent,
  onRefresh,
  isRefreshing = false,
  basePath = '/marketplace/home/agent-chat',
  stsMode = false,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const supportsSession = agentConfig.protocol !== 'json';

  if (stsMode) {
    return (
      <Box className={classes.stsHeader}>
        <Box className={classes.headerLeft}>
          <Avatar className={classes.avatarSmall} style={{ backgroundColor: agentConfig.color }}>
            {getAgentIcon(agentConfig.icon)}
          </Avatar>
          <Box>
            <Typography variant="h6" className={classes.stsTitle}>
              {agentConfig.heading || selectedAgent}<span className={classes.betaBadge}>BETA</span>
            </Typography>
            <Typography variant="caption" className={classes.stsSubtitle}>
              AI Assistant
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          {supportsSession && onRefresh && (
            <IconButton
              className={classes.stsActionButton}
              onClick={onRefresh}
              size="small"
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <CircularProgress size={18} style={{ color: '#616161' }} />
              ) : (
                <DarkTooltip title="New Session" arrow>
                  <RefreshIcon />
                </DarkTooltip>
              )}
            </IconButton>
          )}
          <DarkTooltip title="Agent Admin" arrow>
            <IconButton
              className={classes.stsActionButton}
              onClick={() => navigate(`${basePath}/admin`)}
              size="small"
            >
              <Settings />
            </IconButton>
          </DarkTooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.chatHeader}>
      <Box className={classes.headerLeft}>
        <Avatar
          className={classes.avatarSmall}
          style={{ backgroundColor: agentConfig.color }}
        >
          {getAgentIcon(agentConfig.icon)}
        </Avatar>
        <Typography variant="h6">
          {agentConfig.heading || selectedAgent}
        </Typography>
      </Box>
      <Box className={classes.headerRight}>
        {supportsSession && onRefresh && (
          <IconButton
            className={classes.refreshButton}
            onClick={onRefresh}
            size="small"
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <CircularProgress size={20} style={{ color: '#ffffff' }} />
            ) : (
              <DarkTooltip title="Refresh Session" arrow>
                <RefreshIcon />
              </DarkTooltip>
            )}
          </IconButton>
        )}
        <DarkTooltip title="Agent Admin" arrow>
          <IconButton
            className={classes.themeToggle}
            onClick={() => navigate(`${basePath}/admin`)}
            size="small"
          >
            <Settings />
          </IconButton>
        </DarkTooltip>
      </Box>
    </Box>
  );
};
